// Components/Pages/Admin/Gallery.jsx
import { useState, useEffect } from "react";
import { api } from "./api";
import { FaUpload, FaTrash, FaImage, FaTimes, FaPlus, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    eventName: "",
    description: ""
  });
  const [expandedAlbums, setExpandedAlbums] = useState({});

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const data = await api.getGallery();
      setImages(data);
      // Auto-expand all albums
      const albums = getAlbums(data);
      const expanded = {};
      Object.keys(albums).forEach(album => {
        expanded[album] = true;
      });
      setExpandedAlbums(expanded);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await api.deleteGalleryImage(id);
        await fetchGallery();
        toast.success('Image deleted successfully!');
      } catch (error) {
        console.error('Error deleting image:', error);
        toast.error('Failed to delete image');
      }
    }
  };

  const handleDeleteAlbum = async (albumName) => {
    const albumImages = images.filter(img => img.category === albumName);
    if (window.confirm(`Delete all ${albumImages.length} images in "${albumName}"?`)) {
      try {
        for (const image of albumImages) {
          await api.deleteGalleryImage(image.id);
        }
        await fetchGallery();
        toast.success(`Album "${albumName}" deleted successfully!`);
      } catch (error) {
        console.error('Error deleting album:', error);
        toast.error('Failed to delete album');
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check if all files are images
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      toast.error('Please upload only image files');
      e.target.value = '';
      return;
    }

    setSelectedFiles(files);
    setFormData({
      eventName: "",
      description: ""
    });
    setShowModal(true);
    e.target.value = '';
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitUpload = async () => {
    // Validate event name
    if (!formData.eventName.trim()) {
      toast.error('Please enter an event/album name');
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error('No files selected');
      return;
    }

    setUploading(true);
    const toastId = toast.loading(`Uploading ${selectedFiles.length} images...`);
    
    try {
      let uploadedCount = 0;
      
      for (const file of selectedFiles) {
        const result = await api.uploadFile(file);
        
        // Create gallery entry with uploaded image
        await api.createGalleryImage({
          title: file.name.replace(/\.[^/.]+$/, ""),
          description: formData.description.trim(),
          category: formData.eventName.trim(),
          location: "",
          date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          image_url: result.filepath,
          is_uploaded: true
        });
        uploadedCount++;
      }
      
      toast.update(toastId, {
        render: `${uploadedCount} images uploaded successfully to "${formData.eventName}"!`,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
      
      setShowModal(false);
      setSelectedFiles([]);
      setFormData({ eventName: "", description: "" });
      await fetchGallery();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.update(toastId, {
        render: 'Failed to upload images',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFiles([]);
    setFormData({ eventName: "", description: "" });
  };

  const toggleAlbum = (albumName) => {
    setExpandedAlbums(prev => ({
      ...prev,
      [albumName]: !prev[albumName]
    }));
  };

  // Group images by category (album/event name)
  const getAlbums = (imagesList) => {
    const albums = {};
    imagesList.forEach(img => {
      const albumName = img.category || 'Uncategorized';
      if (!albums[albumName]) {
        albums[albumName] = [];
      }
      albums[albumName].push(img);
    });
    return albums;
  };

  const albums = getAlbums(images);
  const albumNames = Object.keys(albums);

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      {/* Upload Button */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-primaryBlue">Upload Images</h3>
              <p className="text-sm text-gray-500">Select multiple images and group them by event/album name</p>
            </div>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <button 
                className="bg-primaryGold text-primaryBlue px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-all flex items-center gap-2"
                disabled={uploading}
              >
                <FaPlus />
                {uploading ? 'Uploading...' : 'Select Images'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-primaryBlue">Upload Images</h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Selected files preview */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700">
                  {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} selected
                </p>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {selectedFiles.slice(0, 4).map((file, index) => (
                    <div key={index} className="w-full h-16 bg-gray-200 rounded-lg overflow-hidden">
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Preview ${index}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {selectedFiles.length > 4 && (
                    <div className="w-full h-16 bg-gray-300 rounded-lg flex items-center justify-center text-sm font-medium text-gray-600">
                      +{selectedFiles.length - 4} more
                    </div>
                  )}
                </div>
              </div>

              {/* Event/Album Name field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event/Album Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleFormChange}
                  placeholder="e.g., Community Workshop 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                />
                <p className="text-xs text-gray-400 mt-1">All images will be grouped under this name</p>
              </div>

              {/* Description field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Brief description for all images..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={handleSubmitUpload}
                disabled={uploading}
                className="flex-1 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}`}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Grid - Grouped by Album */}
      {images.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FaImage className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No images uploaded yet.</p>
          <p className="text-gray-400 text-sm mt-1">Upload images to get started.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {albumNames.map((albumName) => (
            <div key={albumName} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
              {/* Album Header */}
              <div 
                className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleAlbum(albumName)}
              >
                <div className="flex items-center gap-3">
                  {expandedAlbums[albumName] ? <FaChevronDown className="text-gray-500" /> : <FaChevronRight className="text-gray-500" />}
                  <h3 className="font-bold text-primaryBlue">{albumName}</h3>
                  <span className="text-sm text-gray-500">({albums[albumName]?.length || 0} images)</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAlbum(albumName);
                  }}
                  className="text-red-500 hover:text-red-700 transition-colors text-sm"
                >
                  Delete Album
                </button>
              </div>

              {/* Album Images */}
              {expandedAlbums[albumName] && albums[albumName] && (
                <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {albums[albumName].map((image) => (
                    <div 
                      key={image.id} 
                      className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-all group relative"
                    >
                      <div className="relative h-40 bg-gray-100">
                        {image.image_url ? (
                          <img 
                            src={`https://api.rigokenya.org${image.image_url}`} 
                            alt={image.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x300/1a365d/ffffff?text=Image';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <FaImage className="text-4xl" />
                          </div>
                        )}
                        {/* Delete button overlay */}
                        <button
                          onClick={() => handleDelete(image.id)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                      <div className="p-2">
                        <p className="text-xs text-gray-500 truncate" title={image.title}>
                          {image.title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Gallery;